import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Play, Eye, Sparkles, Scale, FileText, Shield, Scan, 
  BarChart3, Gavel, Users, DollarSign, ArrowRight, Loader2,
  Lock, CheckCircle2
} from "lucide-react";
import { TOOLS } from "@/constants";

const DEMO_WALKTHROUGH = [
  { tool: "litix-wizard", name: "Litix Wizard", icon: Sparkles, desc: "Guided 6-step case onboarding with AI analysis", category: "Premium" },
  { tool: "investigator", name: "The Investigator", icon: Scan, desc: "Deep opposition research and risk profiling", category: "Premium" },
  { tool: "lytix", name: "Lytix Ledger", icon: FileText, desc: "AI-powered document analysis engine", category: "Premium" },
  { tool: "judges", name: "Judge Analytics", icon: Gavel, desc: "Historical patterns from live court data", category: "Analytics" },
  { tool: "strategy", name: "Strategy Patterns", icon: BarChart3, desc: "Litigation pattern analysis and predictions", category: "Analytics" },
  { tool: "opening", name: "Opening Statements", icon: Scale, desc: "AI-generated opening statement drafts", category: "Practice" },
  { tool: "settlement-negotiation", name: "Settlement Suite", icon: DollarSign, desc: "Negotiation strategy and talking points", category: "Settlement" },
  { tool: "insurance-report", name: "Insurance Analysis", icon: Shield, desc: "Coverage analysis and bad faith detection", category: "Insurance" },
  { tool: "services-hub", name: "Services Hub", icon: Users, desc: "11 white-label service networks", category: "Services" },
];

const DemoDashboard = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const toolCategories = {
    premium: TOOLS.filter(t => t.category === "premium"),
    trial: TOOLS.filter(t => t.category === "trial"),
    settlement: TOOLS.filter(t => t.category === "settlement"),
    analytics: TOOLS.filter(t => t.category === "analytics"),
    services: TOOLS.filter(t => t.category === "services"),
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="demo-dashboard">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="h-8 w-8 text-blue-200" />
            <h1 className="text-2xl font-bold">Demo Walkthrough</h1>
            <Badge className="bg-blue-500/30 text-white border-blue-400/30">Read Only</Badge>
          </div>
          <p className="text-blue-100">Explore every feature of the Trialeze platform</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Guided Tour */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Play className="h-5 w-5 text-blue-600" />
              Guided Tour — {DEMO_WALKTHROUGH.length} Key Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {DEMO_WALKTHROUGH.map((step, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveStep(i); navigate(`/tools/${step.tool}`); }}
                  data-testid={`demo-tour-step-${i}`}
                  className={`text-left p-4 rounded-lg border transition-all hover:shadow-md ${
                    activeStep === i ? "border-blue-500 bg-white shadow-sm" : "border-slate-200 bg-white/70"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-md ${activeStep === i ? "bg-blue-100" : "bg-slate-100"}`}>
                      <step.icon className={`h-4 w-4 ${activeStep === i ? "text-blue-600" : "text-slate-500"}`} />
                    </div>
                    <Badge variant="outline" className="text-[10px]">{step.category}</Badge>
                  </div>
                  <p className="font-medium text-sm">{step.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{step.desc}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* All Tools by Category */}
        <div className="space-y-6">
          {Object.entries(toolCategories).map(([category, tools]) => (
            tools.length > 0 && (
              <Card key={category}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base capitalize flex items-center gap-2">
                    {category === "premium" && <Sparkles className="h-4 w-4 text-amber-500" />}
                    {category === "trial" && <Scale className="h-4 w-4 text-indigo-500" />}
                    {category === "settlement" && <DollarSign className="h-4 w-4 text-emerald-500" />}
                    {category === "analytics" && <BarChart3 className="h-4 w-4 text-blue-500" />}
                    {category === "services" && <Users className="h-4 w-4 text-purple-500" />}
                    {category.replace('_', ' ')} Suite ({tools.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {tools.map((tool) => (
                      <Link
                        key={tool.id}
                        to={`/tools/${tool.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
                        data-testid={`demo-tool-${tool.id}`}
                      >
                        <div className={`p-2 rounded-lg ${tool.color} text-white`}>
                          <tool.icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{tool.name}</p>
                          <p className="text-xs text-slate-500 truncate">{tool.desc}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          ))}
        </div>

        {/* Upgrade CTA */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="py-6 text-center">
            <Lock className="h-8 w-8 text-amber-600 mx-auto mb-3" />
            <h3 className="font-bold text-lg mb-1">Ready to unlock full access?</h3>
            <p className="text-sm text-slate-600 mb-4">Request membership to use all tools with your real cases</p>
            <Link to="/contact">
              <Button className="bg-amber-600 hover:bg-amber-700" data-testid="demo-upgrade-btn">
                Request Membership <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoDashboard;
